import { useCallback, useEffect, useState } from 'react'
import { ArrowLeftRight, GitMerge, Search, AlertTriangle, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/store/authStore'
import { apiService, ApercuFusion, ChampFusion, TypeFusion } from '@/services/api'

/* La fusion de deux fiches en double (28/10/2026).

   On part d'une fiche (celle qu'on regarde) ; si l'autre n'est pas connue, on
   la cherche. Les deux fiches s'affichent côte à côte : pour chaque champ où
   elles diffèrent, on coche la valeur à garder, celle de la fiche gardée étant
   cochée d'office ; un champ vide sur la fiche gardée est complété par l'autre
   sans rien demander. La fiche en double part ensuite à la corbeille. */

interface Props {
  type: TypeFusion
  open: boolean
  /** La fiche de départ, gardée par défaut. */
  ficheId: number
  /** L'autre fiche, si on la connaît déjà (l'alerte de doublon la donne). */
  autreId?: number
  onClose: () => void
  /** Après la fusion : le numéro de la fiche qui reste. */
  onFusion: (gardeId: number) => void
}

interface Candidat { id: number; nom: string; detail: string }

const AUTRE = { contact: "l'autre contact", entreprise: "l'autre entreprise" }

export default function FusionDialog({ type, open, ficheId, autreId, onClose, onFusion }: Props) {
  const { toast } = useToast()
  const estAdmin = Boolean(useAuthStore((etat) => etat.user?.is_admin))

  const [gardeId, setGardeId] = useState(ficheId)
  const [doublonId, setDoublonId] = useState<number | null>(autreId ?? null)
  const [recherche, setRecherche] = useState('')
  const [candidats, setCandidats] = useState<Candidat[]>([])
  const [apercu, setApercu] = useState<ApercuFusion | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [choix, setChoix] = useState<Record<string, 'garde' | 'doublon'>>({})
  const [enCours, setEnCours] = useState(false)

  // À chaque ouverture, on repart de la fiche regardée.
  useEffect(() => {
    if (!open) return
    setGardeId(ficheId)
    setDoublonId(autreId ?? null)
    setRecherche('')
    setCandidats([])
    setApercu(null)
    setErreur(null)
    setChoix({})
  }, [open, ficheId, autreId])

  // La recherche de l'autre fiche.
  useEffect(() => {
    if (!open || doublonId !== null || recherche.trim().length < 2) {
      setCandidats([])
      return
    }
    const minuterie = setTimeout(async () => {
      if (type === 'contact') {
        const r = await apiService.getContacts({ search: recherche.trim(), limit: 10 })
        setCandidats((r.data?.data ?? []).filter((c) => c.id !== ficheId).map((c: any) => ({
          id: c.id,
          nom: `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim(),
          detail: [c.email, c.company_name].filter(Boolean).join(' · '),
        })))
      } else {
        const r = await apiService.getCompanies({ search: recherche.trim(), limit: 10 })
        setCandidats((r.data?.data ?? []).filter((c) => c.id !== ficheId).map((c: any) => ({
          id: c.id,
          nom: c.name,
          detail: [c.postal_code, c.city].filter(Boolean).join(' '),
        })))
      }
    }, 300)
    return () => clearTimeout(minuterie)
  }, [open, recherche, doublonId, type, ficheId])

  const chargerApercu = useCallback(async (garde: number, doublon: number) => {
    setApercu(null)
    setErreur(null)
    setChoix({})
    const r = await apiService.getApercuFusion(type, garde, doublon)
    if (r.success && r.data) setApercu(r.data)
    else setErreur(r.error || "Les deux fiches n'ont pas pu être comparées.")
  }, [type])

  useEffect(() => {
    if (open && doublonId !== null) void chargerApercu(gardeId, doublonId)
  }, [open, gardeId, doublonId, chargerApercu])

  const inverser = () => {
    if (doublonId === null) return
    setGardeId(doublonId)
    setDoublonId(gardeId)
  }

  const confirmer = async () => {
    if (!apercu || doublonId === null) return
    setEnCours(true)
    const r = await apiService.fusionner(type, gardeId, doublonId, choix)
    setEnCours(false)
    if (!r.success || !r.data) {
      toast({ title: 'La fusion a échoué', description: r.error, variant: 'destructive' })
      return
    }
    const deplaces = r.data.contacts_deplaces
    toast({
      title: 'Fiches fusionnées',
      description: [
        `« ${apercu.doublon.nom} » a rejoint « ${r.data.nom} » et se trouve dans la corbeille.`,
        deplaces ? `${deplaces} contact${deplaces > 1 ? 's ont' : ' a'} été rattaché${deplaces > 1 ? 's' : ''} à la fiche gardée.` : '',
      ].filter(Boolean).join(' '),
      variant: 'success',
    })
    for (const a of r.data.avertissements) {
      toast({ title: 'À savoir', description: a })
    }
    onFusion(gardeId)
  }

  const visibles = apercu?.champs.filter((c) => c.etat !== 'vide') ?? []
  const conflits = visibles.filter((c) => c.etat === 'conflit').length
  const interdit = Boolean(apercu?.admin_requis && !estAdmin)

  const cellule = (ch: ChampFusion, cote: 'garde' | 'doublon') => {
    const valeur = cote === 'garde' ? ch.affichage_garde : ch.affichage_doublon
    if (ch.etat === 'conflit') {
      const retenu = (choix[ch.cle] ?? 'garde') === cote
      return (
        <label className={`flex items-start gap-2 rounded-lg px-2 py-1.5 cursor-pointer ${retenu ? 'bg-blue-50 ring-1 ring-blue-300' : 'hover:bg-gray-50'}`}>
          <input
            type="radio"
            className="mt-1"
            name={`choix-${ch.cle}`}
            checked={retenu}
            onChange={() => setChoix((p) => ({ ...p, [ch.cle]: cote }))}
          />
          <span className="break-words">{valeur}</span>
        </label>
      )
    }
    if (ch.etat === 'complete' && cote === 'doublon') {
      return (
        <span className="px-2 py-1.5 block break-words">
          {valeur} <Badge variant="secondary" className="ml-1">repris</Badge>
        </span>
      )
    }
    return <span className={`px-2 py-1.5 block break-words ${valeur ? '' : 'text-gray-400'}`}>{valeur ?? 'Vide'}</span>
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !enCours && onClose()}>
      <DialogContent className="sm:max-w-[760px] max-h-[90vh] flex flex-col rounded-2xl sm:rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="w-5 h-5 text-blue-600" />
            Fusionner deux fiches en double
          </DialogTitle>
          <DialogDescription>
            La fiche gardée reste ; l’autre lui transmet ce qui lui manque, puis part à la corbeille, d’où un administrateur
            peut la restaurer.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {doublonId === null ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">Cherchez {AUTRE[type]}, la fiche qui fait double emploi :</p>
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  autoFocus
                  className="pl-9"
                  placeholder={type === 'contact' ? 'Nom, prénom ou adresse email' : "Nom de l'entreprise"}
                  value={recherche}
                  onChange={(e) => setRecherche(e.target.value)}
                />
              </div>
              <div className="border rounded-lg divide-y">
                {candidats.length === 0 ? (
                  <p className="text-sm text-gray-500 p-3">
                    {recherche.trim().length < 2 ? 'Tapez au moins deux lettres.' : 'Aucune fiche ne correspond.'}
                  </p>
                ) : candidats.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="w-full text-left p-3 hover:bg-blue-50"
                    onClick={() => setDoublonId(c.id)}
                  >
                    <div className="font-medium">{c.nom} <span className="text-xs text-gray-400">n° {c.id}</span></div>
                    {c.detail && <div className="text-xs text-gray-500">{c.detail}</div>}
                  </button>
                ))}
              </div>
            </div>
          ) : erreur ? (
            <div className="rounded-lg bg-red-50 text-red-700 p-3 text-sm">{erreur}</div>
          ) : !apercu ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                <div className="rounded-xl border-2 border-green-300 bg-green-50 p-3">
                  <div className="text-xs font-bold text-green-700 uppercase">Fiche gardée</div>
                  <div className="font-semibold">{apercu.garde.nom} <span className="text-xs text-gray-400">n° {apercu.garde.id}</span></div>
                  {apercu.garde.detail && <div className="text-xs text-gray-600">{apercu.garde.detail}</div>}
                </div>
                <Button variant="outline" size="sm" onClick={inverser} title="Garder plutôt l'autre fiche" disabled={enCours}>
                  <ArrowLeftRight className="w-4 h-4" />
                </Button>
                <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3">
                  <div className="text-xs font-bold text-red-700 uppercase">Part à la corbeille</div>
                  <div className="font-semibold">{apercu.doublon.nom} <span className="text-xs text-gray-400">n° {apercu.doublon.id}</span></div>
                  {apercu.doublon.detail && <div className="text-xs text-gray-600">{apercu.doublon.detail}</div>}
                </div>
              </div>

              {apercu.blocage && (
                <div className="flex gap-2 rounded-lg bg-red-50 text-red-700 p-3 text-sm">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{apercu.blocage}
                </div>
              )}
              {!apercu.blocage && interdit && (
                <div className="flex gap-2 rounded-lg bg-amber-50 text-amber-800 p-3 text-sm">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  Une des deux fiches porte un compte UP-SPOT : seul un administrateur peut les fusionner.
                </div>
              )}

              <p className="text-sm text-gray-600">
                {conflits === 0
                  ? 'Les deux fiches ne se contredisent sur aucun champ : rien à choisir.'
                  : conflits === 1
                    ? 'Un champ diffère : cochez la valeur à garder.'
                    : `${conflits} champs diffèrent : cochez, pour chacun, la valeur à garder.`}
              </p>

              <div className="border rounded-lg overflow-hidden text-sm">
                <div className="grid grid-cols-[9rem_1fr_1fr] bg-gray-50 font-semibold text-gray-600 text-xs uppercase">
                  <div className="px-3 py-2">Champ</div>
                  <div className="px-3 py-2">Fiche gardée</div>
                  <div className="px-3 py-2">Fiche en double</div>
                </div>
                {visibles.map((ch) => (
                  <div key={ch.cle} className={`grid grid-cols-[9rem_1fr_1fr] border-t items-start ${ch.etat === 'conflit' ? 'bg-amber-50/40' : ''}`}>
                    <div className="px-3 py-2 text-gray-500">{ch.libelle}</div>
                    <div className="px-1 py-0.5">{cellule(ch, 'garde')}</div>
                    <div className="px-1 py-0.5">{cellule(ch, 'doublon')}</div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 text-sm text-gray-600">
                <Info className="w-4 h-4 shrink-0 mt-0.5 text-blue-500" />
                <div className="space-y-1">
                  {visibles.some((c) => c.etat === 'complete') && (
                    <p>Les valeurs marquées « repris » manquent à la fiche gardée : elles lui sont recopiées.</p>
                  )}
                  {type === 'entreprise' && Boolean(apercu.doublon.nb_contacts) && (
                    <p>
                      {apercu.doublon.nb_contacts === 1
                        ? 'Le contact de la fiche en double sera rattaché à la fiche gardée.'
                        : `Les ${apercu.doublon.nb_contacts} contacts de la fiche en double seront rattachés à la fiche gardée.`}
                    </p>
                  )}
                  {type === 'contact' && apercu.doublon.compte_upspot && !apercu.garde.compte_upspot && (
                    <p>Le compte UP-SPOT de la fiche en double suivra la fiche gardée et prendra son nom et son adresse email.</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          {doublonId !== null && !autreId && (
            <Button variant="ghost" onClick={() => { setDoublonId(null); setApercu(null); setGardeId(ficheId) }} disabled={enCours}>
              Choisir une autre fiche
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={enCours}>Annuler</Button>
          <Button
            onClick={() => void confirmer()}
            disabled={!apercu || Boolean(apercu.blocage) || interdit || enCours}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <GitMerge className="w-4 h-4 mr-2" />
            {enCours ? 'Fusion…' : 'Fusionner'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
